// src/App.js
import React, {useEffect, useState} from 'react';
import OrderService from '../services/OrderService';
import KlineService from '../services/KlineService';
import {useParams} from 'react-router-dom';
import Spinner from "./Spinner";
import {intervalToMilliseconds} from "../commons/utils";
import {ChartComponent} from "./ChartComponent";
import { toZonedTime } from 'date-fns-tz';
import TradeHistoryCard from "./TradeHistoryCard";
import { timeZonesNames } from '@vvo/tzdb';
import Select from 'react-select';

function OrderComponent() {
  const [order, setOrder] = useState(null);
  const [orgOrder, setOrgOrder] = useState(null);
  const [error, setError] = useState(null);
  const [symbols, setSymbols] = useState(null);
  const [symbol, setSymbol] = useState(null);
  const [exchange, setExchange] = useState("binance");
  const [direction, setDirection] = useState("LONG_SHORT");
  const [timeframeList, setTimeframeList] = useState(["1M", "3M", "5M", "15M", "30M", "1H", "2H", "4H", "1D"]);
  const [timeframe, setTimeframe] = useState(null);
  const [accumulation, setAccumulation] = useState("Add");
  const [timezone, setTimezone] = useState("Africa/Accra");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [minOpenTimestamp, setMinOpenTimestamp] = useState(null);
  const [maxCloseTimestamp, setMaxCloseTimestamp] = useState(null);
  const [klineData, setKlineData] = useState([]);
  const [orgKlineData, setOrglineData] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [profits, setProfits] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [indicatorNames, setIndicatorNames] = useState([]);
  const [colorMap, setColorMap] = useState(new Map());
  const {ref} = useParams();

  useEffect(() => {
    OrderService.fetchOrder(ref, setOrgOrder, setError);
  }, [ref]);

  useEffect(() => {
    if (order) {
      const allSymbols = Array.from(new Set(order.orders.map(order => order.symbol)));
      if (order.indicators) {
        order.indicators.forEach(indicator => {
          if (allSymbols.indexOf(indicator.symbol) === -1) {
            allSymbols.push(indicator.symbol);
          }
        });
      }
      setSymbols(allSymbols);
      if (allSymbols.length > 0) {
        setSymbol(allSymbols[0]);
        setTimeframe(order.settings.interval.replace("_", ""));
      }
      if (order.exchange) {
        setExchange(order.exchange);
        if (order.exchange === "vnstock" || order.exchange === "vndirect") {
          setTimeframeList(["1M", "5M", "15M", "30M", "1H", "1D"]);
        }
      }
    }
  }, [order]);

  useEffect(() => {
    if (order) {
      const filtered = order.orders.filter(o =>
        (o.symbol === symbol)
      );
      const allDirections = [];
      if (filtered.length > 0 && direction !== 'LONG_SHORT') {
        filtered.forEach(o => {if (!allDirections.includes(o.direction)) {allDirections.push(o.symbol)}});
        if (!allDirections.includes(direction)) {
          setDirection(filtered[0].direction);
        }
      }
      //
      if (order.indicators) {
        const colorList = ['#26a69a','#801dad', '#1d27ad', '#ad1d8c'];
        const colorMap = new Map();
        let linePriceColorCounter = 0;
        let nonLinePriceColorCounter = 0;

        const indicatorNameList = [];
        order.indicators.forEach(indicator => {
          if (symbol.toLowerCase() === indicator.symbol.toLowerCase()) {
            indicatorNameList.push(indicator.name);
            colorMap.set(indicator.name, colorList[(indicator.usePriceScale ? linePriceColorCounter++ : nonLinePriceColorCounter++) % colorList.length]);
          }
        });
        setIndicatorNames(indicatorNameList);
        setSelectedIndicators(indicatorNameList);
        setColorMap(colorMap);
      }
    }
  }, [symbol]);

  const clear = () => {
    setKlineData(null);
    setMinOpenTimestamp(null);
    setMaxCloseTimestamp(null);
    setMarkers(null);
    setProfits(null);
    setIndicators(null);
  }

  const convertTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const zonedDate = toZonedTime(date, timezone);
    return zonedDate.getTime() / 1000;
  };
  const convertTimepouch = (timestamp) => {
    const date = new Date(timestamp);
    const zonedDate = toZonedTime(date, timezone);
    return zonedDate.getTime();
  };

  useEffect(() => {
    if (orgKlineData.length === 0) return;
    const converted = orgKlineData.map((item) => {
      return {
        ...item,
        time: convertTimestamp(item.time),
      };
    });
    setKlineData(converted);
  }, [orgKlineData, timezone]);

  useEffect(() => {
    if (!orgOrder) return;

    // Convert order timestamps
    const newOrder = {
      ...orgOrder,
      orders: orgOrder.orders.map(o => ({
        ...o,
        openTimestamp: convertTimepouch(o.openTimestamp),
        closeTimestamp: convertTimepouch(o.closeTimestamp),
      })),
      indicators: orgOrder.indicators?.map(ind => ({
        ...ind,
        data: ind.data.map(d => ({
          ...d,
          t: convertTimepouch(d.t),
        })),
      })),
    };
    setOrder(newOrder);
  }, [orgOrder, timezone]);

  useEffect(() => {
    if (order && symbol && direction) {
      const orders = order.orders || [];
      const filtered = orders.filter(o =>
        (symbol ? o.symbol === symbol : true) &&
        (direction && direction !== 'LONG_SHORT' ? o.direction === direction : true)
      ).sort((a, b) => a.closeTimestamp - b.closeTimestamp);
      setFilteredOrders(filtered);

      const settingIntervalMs = intervalToMilliseconds(order.settings.interval.replace("_", "")) * 200;
      // klinedata
      let minOpen = Math.min(...filtered.map(o => o.openTimestamp)) - settingIntervalMs;
      let maxClose = Math.max(...filtered.map(o => o.closeTimestamp)) + settingIntervalMs;
      if (order.indicators) {
        order.indicators.forEach(indicator => {
          indicator.data.forEach(o => {
            minOpen = Math.min(minOpen, o.t); 
          });
          indicator.data.forEach(o => {
            maxClose = Math.max(maxClose, o.t);
          });
        });
      }
      if (!order.indicators && filtered.length === 0) {
        clear();
        return;
      }
      KlineService.fetchData(symbol, exchange, timeframe, minOpen, maxClose, setOrglineData, setError);
      setMinOpenTimestamp(minOpen);
      setMaxCloseTimestamp(maxClose);
      // markers
      let markerList = [];
      filtered.forEach(o => {
        const color = o.profitRate > 0 ? 'green' : 'red';
        const openTime = (o.openTimestamp + 1) / 1000;
        for (let i = 0; i < 100; i++) {
          if (markerList.filter(value => value.time === openTime + i).length === 0) {
            markerList.push({
              time: openTime + i,
              position: 'belowBar',
              color,
              shape: 'arrowUp',
              text: 'In',
            });
            break;
          }
        }

        const closeTime = (o.closeTimestamp + 1) / 1000;
        for (let i = 0; i < 100; i++) {
          if (markerList.filter(value => value.time === closeTime + i).length === 0) {
            markerList.push({
              time: closeTime + i,
              position: 'aboveBar',
              color,
              shape: 'arrowDown',
              text: 'Out',
            });
            break;
          }
        }
      });
      markerList = markerList.sort((a, b) => a.time - b.time);
      setMarkers(markerList);
      // profit
      const profitList = [];
      filtered.forEach(o => {
        if (profitList.length === 0) {
          profitList.push({
            time: (o.closeTimestamp + 1) / 1000,
            value: o.profitRate
          });
        } else {
          let newProfit = profitList[profitList.length - 1].value;
          if (accumulation === "Add") {
            newProfit += o.profitRate;
          } else if (accumulation === "Mul") {
            newProfit = (newProfit + 1) * (o.profitRate + 1) - 1;
          }
          const newTimestamp = (o.closeTimestamp + 1) / 1000;
          if (profitList[profitList.length - 1].time === newTimestamp) {
            profitList[profitList.length - 1].value = newProfit;
          } else {
            profitList.push({
              time: newTimestamp,
              value: newProfit
            })
          }
        }
      });
      setProfits(profitList);
      // indicators
      setIndicators(order.indicators);
    } else {
      clear();
    }
  }, [symbol, direction, order, timeframe, accumulation]);

  if (error) return <div style={{color: 'red'}}>{error}</div>;
  if (!order || !symbols) return <Spinner text="Loading order"/>;
  if (!filteredOrders) return <Spinner text="Filtering orders"/>;


  const selectSymbol = () => {
    return <div>
      <select className="form-select m-1" id="symbol-select" value={symbol} onChange={e => {
        setSymbol(e.target.value)
      }}>
        <option value="">Select Symbol</option>
        {symbols.map((symbol, index) => (
          <option key={index} value={symbol}>{symbol}</option>
        ))}
      </select>
    </div>
  };

  const selectDirection = () => {
    return <div>
      <select className="form-select m-1" id="symbol-select" value={direction} onChange={e => {
        setDirection(e.target.value)
      }}>
        <option value="">Direction</option>
        {["LONG", "SHORT", "LONG_SHORT"].map((direction, index) => (
          <option key={index} value={direction}>{direction}</option>
        ))}
      </select>
    </div>;
  }

  const selectTimeframe = () => {
    return <div>
      <select className="form-select m-1" id="timeframe-select" value={timeframe} onChange={e => {
        setTimeframe(e.target.value)
      }}>
        <option value="">Timeframe</option>
        {timeframeList.map((timeframe, index) => (
          <option key={index} value={timeframe}>{timeframe}</option>
        ))}
      </select>
    </div>;
  }

  const selectAccumulation = () => {
    return <div>
      <select className="form-select m-1" id="accumulation-select" value={accumulation} onChange={e => {
        setAccumulation(e.target.value)
      }}>
        <option value="">Accumulation</option>
        {["Add", "Mul"].map((accumulation, index) => (
          <option key={index} value={accumulation}>{accumulation}</option>
        ))}
      </select>
    </div>;
  }

  const selectTimezone = () => {
    const options = timeZonesNames.map(name => ({
      value: name,
      label: name
    }));

    return (
      <div className="m-1"  style={{ position: 'relative', zIndex: 10 }}>
        <Select
          id="timezone-select"
          options={options}
          placeholder="select timezone"
          value={options.find(o => o.value === timezone)}
          onChange={option => setTimezone(option?.value || '')}
          isClearable
          styles={{
            container: (base) => ({ ...base, width: 300 }),  // fixed width for select control
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>
    );
  };

  const selectIndicators = () => {
    const handleIndicatorChange = (e) => {
      const indicator = e.target.value;
      let updatedSelectedIndicators = [...selectedIndicators]; // create a copy of the current state

      if (e.target.checked) {
        // Add indicator if checkbox is checked and not already in array
        if (!updatedSelectedIndicators.includes(indicator)) {
          updatedSelectedIndicators.push(indicator);
        }
      } else {
        // Remove indicator if checkbox is unchecked
        updatedSelectedIndicators = updatedSelectedIndicators.filter(
          (item) => item !== indicator
        );
      }

      setSelectedIndicators(updatedSelectedIndicators); // update state with the new array
    };

    return (
      <div>
        <div className="d-flex flex-row">
          {indicatorNames.map((indicator, index) => (
            <div key={index} className="form-check m-1">
              <input
                className="form-check-input"
                type="checkbox"
                id={`indicator-${index}`}
                value={indicator}
                checked={selectedIndicators.includes(indicator)} // check if it's in the Set
                onChange={handleIndicatorChange} // handle checkbox change
              />
              <label className="form-check-label" htmlFor={`indicator-${index}`} style={{ color: colorMap.get(indicator) }}>
                {indicator}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>

      <table>
        <tr>
          <td><h5>{ref}</h5></td>
          <td colSpan="4">
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'nowrap',    // prevent wrapping to next line
                width: '100%',
              }}
            >
              {selectSymbol()}
              {selectDirection()}
              {selectTimeframe()}
              {selectAccumulation()}
              {selectTimezone()}
            </div>
          </td>
        </tr>
        <tr>
          <td>Indicators</td>
          <td colSpan="4">{selectIndicators()}</td>
        </tr>
      </table>

      <div>
        <p>Display
          for {symbol} {direction} {minOpenTimestamp} {maxCloseTimestamp} {timeframe} {filteredOrders.length} orders</p>
        </div>
        <ChartComponent symbol={symbol}
                        data={klineData}
                        markers={markers}
                        profits={profits}
                        indicators={indicators}
                        timeframe={timeframe}
                        selectedIndicators={selectedIndicators}
                        colorMap={colorMap}></ChartComponent>
        <TradeHistoryCard trades={filteredOrders}/>
    </div>
);

}

export default OrderComponent;
